import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Calendar, SlotInfo, Event } from "react-big-calendar";
import dayjs, { Dayjs } from "dayjs";
import { Input, Row, Col, Typography, Modal, TimePicker, Space } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { TeamMember, TimeBlock, migrateLegacySchedule } from "../../../types/admin/TeamTypes";
import { fromPickerValue, wallClockParts } from "../../../utils/schedule/wallClock";
import { useNotifications } from "../../../context/NotificationContext";
import { SectionCard } from "../../../components/shared";
import { calendarLocalizer } from "../../healthcare/appointments/components/calendarLocalizer";
import UserProfileScheduleOverrides from "./UserProfileScheduleOverrides";

const { Text } = Typography;

interface UserProfileScheduleProps {
	teamMember: TeamMember;
	isEditing: boolean;
	setTeamMember: React.Dispatch<React.SetStateAction<TeamMember | null>>;
}

interface CalendarEvent extends Event {
	id: string;
	dayOfWeek: string;
	blockId: string;
}

const getDayName = (date: Date): string => {
	const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
	return days[date.getDay()];
};

const createReferenceDate = (dayName: string, hours: number, minutes: number): Date => {
	const dayMap: { [key: string]: number } = {
		Sunday: 0,
		Monday: 1,
		Tuesday: 2,
		Wednesday: 3,
		Thursday: 4,
		Friday: 5,
		Saturday: 6,
	};

	// A Monday, which is where the shared localizer starts the week — Sunday lands inside it, not past its end.
	const referenceDate = new Date(2024, 0, 1);
	const targetDay = dayMap[dayName];
	const currentDay = referenceDate.getDay();
	const daysToAdd = (targetDay - currentDay + 7) % 7;

	const result = new Date(referenceDate);
	result.setDate(result.getDate() + daysToAdd);
	result.setHours(hours, minutes, 0, 0);

	return result;
};

const convertBlocksToEvents = (schedule: { [key: string]: TimeBlock[] }): CalendarEvent[] => {
	const events: CalendarEvent[] = [];

	Object.entries(schedule).forEach(([dayName, blocks]) => {
		blocks.forEach((block) => {
			const startParts = wallClockParts(block.start);
			const endParts = wallClockParts(block.end);

			if (startParts && endParts) {
				const eventStart = createReferenceDate(dayName, startParts.hours, startParts.minutes);

				const eventEnd = createReferenceDate(dayName, endParts.hours, endParts.minutes);

				events.push({
					id: `${dayName}-${block.id}`,
					title: "Available",
					start: eventStart,
					end: eventEnd,
					dayOfWeek: dayName,
					blockId: block.id,
				});
			}
		});
	});

	return events;
};

const UserProfileSchedule: React.FC<UserProfileScheduleProps> = ({ teamMember, isEditing, setTeamMember }) => {
	const { showSuccess } = useNotifications();
	const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date; dayOfWeek: string } | null>(null);
	const [showAddModal, setShowAddModal] = useState(false);
	const [newBlockStart, setNewBlockStart] = useState<Dayjs | null>(null);
	const [newBlockEnd, setNewBlockEnd] = useState<Dayjs | null>(null);

	useEffect(() => {
		if (teamMember.team_member_info?.availability_schedule) {
			const schedule = teamMember.team_member_info.availability_schedule;
			const needsMigration = Object.values(schedule).some((dayData) => dayData && !Array.isArray(dayData) && typeof dayData === "object");

			if (needsMigration) {
				const migratedSchedule = migrateLegacySchedule(schedule);
				setTeamMember((prev) => {
					if (!prev) return null;
					return {
						...prev,
						team_member_info: {
							...prev.team_member_info,
							availability_schedule: migratedSchedule,
						},
					};
				});
			}
		}
	}, []);

	const schedule = teamMember.team_member_info?.availability_schedule || {};
	const events = useMemo(() => convertBlocksToEvents(schedule), [schedule]);

	const { defaultDate, min, max } = useMemo(() => {
		const refDate = new Date(2024, 0, 1);
		return {
			defaultDate: refDate,
			min: new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 0, 0, 0),
			max: new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 23, 59, 59),
		};
	}, []);

	const handleSelectSlot = useCallback(
		(slotInfo: SlotInfo) => {
			if (!isEditing) return;

			const dayOfWeek = getDayName(slotInfo.start);
			setSelectedSlot({
				start: slotInfo.start,
				end: slotInfo.end,
				dayOfWeek,
			});
			setNewBlockStart(dayjs(slotInfo.start));
			setNewBlockEnd(dayjs(slotInfo.end));
			setShowAddModal(true);
		},
		[isEditing]
	);

	const handleAddBlock = () => {
		if (!selectedSlot || !newBlockStart || !newBlockEnd) return;

		const newBlock: TimeBlock = {
			id: `block-${Date.now()}`,
			start: fromPickerValue(newBlockStart),
			end: fromPickerValue(newBlockEnd),
		};

		setTeamMember((prev) => {
			if (!prev) return null;

			const currentBlocks = schedule[selectedSlot.dayOfWeek] || [];

			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					availability_schedule: {
						...schedule,
						[selectedSlot.dayOfWeek]: [...currentBlocks, newBlock],
					},
				},
			};
		});

		showSuccess("Availability block added");
		setShowAddModal(false);
		setSelectedSlot(null);
		setNewBlockStart(null);
		setNewBlockEnd(null);
	};

	const handleSelectEvent = useCallback(
		(event: CalendarEvent) => {
			if (!isEditing) return;

			Modal.confirm({
				title: "Delete Availability Block",
				content: `Remove availability on ${event.dayOfWeek} from ${dayjs(event.start).format("h:mm A")} to ${dayjs(event.end).format("h:mm A")}?`,
				okText: "Delete",
				okType: "danger",
				onOk: () => {
					setTeamMember((prev) => {
						if (!prev) return null;

						const currentBlocks = schedule[event.dayOfWeek] || [];
						const updatedBlocks = currentBlocks.filter((block) => block.id !== event.blockId);

						return {
							...prev,
							team_member_info: {
								...prev.team_member_info,
								availability_schedule: {
									...schedule,
									[event.dayOfWeek]: updatedBlocks,
								},
							},
						};
					});

					showSuccess("Availability block removed");
				},
			});
		},
		[isEditing, schedule, setTeamMember, showSuccess]
	);

	const eventStyleGetter = () => {
		return {
			style: {
				backgroundColor: "var(--mg-primary)",
				borderRadius: "4px",
				opacity: 0.8,
				color: "white",
				border: "none",
				display: "block",
			},
		};
	};

	const handleConsultationFeeChange = (value: string) => {
		setTeamMember((prev) => {
			if (!prev) return null;
			const numericValue = value ? parseFloat(value) : 0;
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					consultation_fee: isNaN(numericValue) ? 0 : numericValue,
				},
			};
		});
	};

	const handlePracticingLocationsChange = (value: string) => {
		const locations = value
			.split(",")
			.map((item) => item.trim())
			.filter((item) => item);
		setTeamMember((prev) => {
			if (!prev) return null;
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					practicing_locations: locations,
				},
			};
		});
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* Consultation Settings */}
			<SectionCard title="Consultation Settings">
				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<Text type="secondary" style={{ fontSize: 11 }}>Consultation Fee (AUD)</Text>
						{isEditing ? (
							<Input value={String(teamMember.team_member_info?.consultation_fee || "")} onChange={(e) => handleConsultationFeeChange(e.target.value)} placeholder="0.00" style={{ marginTop: 4 }} />
						) : (
							<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>${typeof teamMember.team_member_info?.consultation_fee === "number" ? teamMember.team_member_info.consultation_fee.toFixed(2) : "0.00"}</Text>
						)}
					</Col>

					<Col xs={24} md={12}>
						<Text type="secondary" style={{ fontSize: 11 }}>Practicing Locations</Text>
						{isEditing ? (
							<Input value={teamMember.team_member_info?.practicing_locations?.join(", ") || ""} onChange={(e) => handlePracticingLocationsChange(e.target.value)} placeholder="e.g., Melbourne, Sydney" style={{ marginTop: 4 }} />
						) : (
							<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{teamMember.team_member_info?.practicing_locations?.join(", ") || "—"}</Text>
						)}
					</Col>
				</Row>
			</SectionCard>

			{/* Calendar */}
			<SectionCard
				title="Weekly Availability"
				extra={
					isEditing && (
						<Space>
							<InfoCircleOutlined style={{ color: "var(--mg-text-secondary)" }} />
							<Text type="secondary" style={{ fontSize: 12 }}>
								Click and drag to add availability blocks
							</Text>
						</Space>
					)
				}
				noPadding
			>
				<div style={{ padding: 20, height: "600px" }}>
					<Calendar
						localizer={calendarLocalizer}
						events={events}
						defaultView="week"
						views={["week"]}
						defaultDate={defaultDate}
						min={min}
						max={max}
						selectable={isEditing}
						onSelectSlot={handleSelectSlot}
						onSelectEvent={handleSelectEvent}
						eventPropGetter={eventStyleGetter}
						step={15}
						timeslots={4}
						toolbar={false}
						formats={{
							dayFormat: "ddd",
							timeGutterFormat: "h A",
						}}
					/>
				</div>
			</SectionCard>

			{isEditing && (
				<UserProfileScheduleOverrides
					teamMember={teamMember}
					isEditing={isEditing}
					onOverridesChange={() => {
						// Optionally reload team member data
					}}
				/>
			)}

			<Modal
				title={`Add Availability - ${selectedSlot?.dayOfWeek}`}
				open={showAddModal}
				onOk={handleAddBlock}
				onCancel={() => {
					setShowAddModal(false);
					setSelectedSlot(null);
					setNewBlockStart(null);
					setNewBlockEnd(null);
				}}
				okText="Add Block"
			>
				<Space direction="vertical" style={{ width: "100%" }} size="large">
					<div>
						<Text strong>Start Time</Text>
						<TimePicker value={newBlockStart} onChange={setNewBlockStart} format="h:mm A" use12Hours minuteStep={15} style={{ width: "100%", marginTop: 8 }} />
					</div>
					<div>
						<Text strong>End Time</Text>
						<TimePicker value={newBlockEnd} onChange={setNewBlockEnd} format="h:mm A" use12Hours minuteStep={15} style={{ width: "100%", marginTop: 8 }} />
					</div>
				</Space>
			</Modal>
		</div>
	);
};

export default UserProfileSchedule;
